import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { format } from 'date-fns'
import { MarkdownArticle } from '@/components/help/MarkdownArticle'
import { findArticle, helpArticles } from '@/content/help/registry'
import { BRAND_MINT } from '@/lib/utils'
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'

export const Route = createFileRoute('/dashboard/help/$slug')({
  component: HelpArticlePage,
})

function HelpArticlePage() {
  const { slug } = useParams({ from: '/dashboard/help/$slug' })
  const article = findArticle(slug)

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-10 text-center">
        <p className="text-lg font-semibold text-foreground">Guide not found</p>
        <p className="mt-1 text-sm text-muted-foreground">That how-to guide doesn't exist.</p>
        <Link
          to="/dashboard/help"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-80"
        >
          <ArrowLeftIcon className="size-4" /> Back to Help &amp; training
        </Link>
      </div>
    )
  }

  const index = helpArticles.findIndex((a) => a.slug === article.slug)
  const prev = index > 0 ? helpArticles[index - 1] : null
  const next = index < helpArticles.length - 1 ? helpArticles[index + 1] : null

  return (
    <article className="mx-auto max-w-3xl">
      <Link
        to="/dashboard/help"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeftIcon className="size-3.5" /> Help &amp; training
      </Link>

      <div className="mb-6 flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 shrink-0"
          style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
        />
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">{article.title}</h1>
          <p className="text-sm text-muted-foreground">
            Updated {format(new Date(`${article.updatedAt}T00:00:00`), 'd MMM yyyy')}
          </p>
        </div>
      </div>

      <MarkdownArticle content={article.content} />

      <nav className="mt-10 flex items-center justify-between gap-4 border-t border-border pt-6">
        {prev ? (
          <Link
            to="/dashboard/help/$slug"
            params={{ slug: prev.slug }}
            className="group flex min-w-0 flex-1 items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeftIcon className="size-4 shrink-0" />
            <span className="truncate">{prev.title}</span>
          </Link>
        ) : (
          <span className="flex-1" />
        )}
        {next ? (
          <Link
            to="/dashboard/help/$slug"
            params={{ slug: next.slug }}
            className="group flex min-w-0 flex-1 items-center justify-end gap-2 text-right text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <span className="truncate">{next.title}</span>
            <ArrowRightIcon className="size-4 shrink-0" />
          </Link>
        ) : (
          <span className="flex-1" />
        )}
      </nav>
    </article>
  )
}